import { useMemo, useState } from 'react';
import {
  Loader2,
  Radio,
  HardDrive,
  ExternalLink,
  Info,
  Plus,
  Trash2,
  Download,
  Upload,
  ChevronUp,
  ChevronDown,
  RotateCcw,
} from 'lucide-react';
import { useAppContext } from '@/hooks/useAppContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNetworkListsSync } from '@/hooks/useNetworkListsSync';
import { useUserNetworkLists } from '@/hooks/useUserNetworkLists';
import {
  APP_RELAYS,
  dedupeRelays,
  dedupeUrls,
  getEffectiveRelays,
  isStockAppRelayConfig,
  isUsingAppRelayFallback,
  normalizeNetworkUrl,
  parseBlossomUrlInput,
  parseRelayUrlInput,
  type RelayEntry,
} from '@/lib/appRelays';
import { cn } from '@/lib/utils';

function hostLabel(url: string): string {
  try {
    const u = new URL(url);
    return u.host || url;
  } catch {
    return url.replace(/^wss?:\/\//i, '').replace(/^https?:\/\//i, '').replace(/\/+$/, '') || url;
  }
}

function formatWhen(createdAt: number | undefined): string | null {
  if (!createdAt) return null;
  try {
    return new Date(createdAt * 1000).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return null;
  }
}

function RwToggle({
  label,
  on,
  onChange,
  disabled,
}: {
  label: string;
  on: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(!on)}
      className={cn(
        'inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium uppercase tracking-wide border transition-colors',
        on
          ? label === 'write'
            ? 'bg-[#2a1a1a] text-[#ba8a7a] border-[#3a2a2a]'
            : 'bg-[#1a1a2a] text-[#7a8aba] border-[#2a2a3a]'
          : 'bg-[#141414] text-[#3a3a3a] border-[#242424] hover:border-[#333]',
        disabled && 'opacity-50 cursor-not-allowed',
      )}
      title={`${label}: ${on ? 'on' : 'off'}`}
    >
      {label}
    </button>
  );
}

/**
 * Settings → Relays: full editor for the live connection pool (NIP-65 shape)
 * and Blossom servers (kind 10063), with pull/publish to the user's pubkey.
 */
export function RelaysSettingsPanel() {
  const { user } = useCurrentUser();
  const { config, updateConfig } = useAppContext();
  const { data: remote, isFetching: remoteFetching } = useUserNetworkLists();
  const {
    busy,
    error,
    status,
    clearMessages,
    pullFromRelays,
    publishRelays,
    publishBlossom,
    usingAppFallback,
  } = useNetworkListsSync();

  const [relayInput, setRelayInput] = useState('');
  const [blossomInput, setBlossomInput] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const relays = config.relayMetadata.relays;
  const blossomServers = config.blossomServers ?? [];
  const effective = useMemo(() => getEffectiveRelays(config.relayMetadata), [config.relayMetadata]);
  const onAppFallback =
    usingAppFallback ||
    isUsingAppRelayFallback(config.relayMetadata) ||
    isStockAppRelayConfig(config.relayMetadata);

  const setRelays = (next: RelayEntry[]) => {
    const cleaned = dedupeRelays(next);
    updateConfig((c) => ({
      ...c,
      relayMetadata: {
        relays: cleaned,
        updatedAt: Math.floor(Date.now() / 1000),
      },
    }));
  };

  const setBlossom = (next: string[]) => {
    updateConfig((c) => ({
      ...c,
      blossomServers: dedupeUrls(next),
    }));
  };

  const addRelay = () => {
    setLocalError(null);
    clearMessages();
    const url = parseRelayUrlInput(relayInput);
    if (!url) {
      setLocalError('Enter a valid relay URL (wss://…)');
      return;
    }
    const key = normalizeNetworkUrl(url);
    if (relays.some((r) => normalizeNetworkUrl(r.url) === key)) {
      setLocalError('That relay is already in the list');
      return;
    }
    setRelays([...relays, { url, read: true, write: true }]);
    setRelayInput('');
  };

  const updateRelay = (index: number, patch: Partial<RelayEntry>) => {
    setLocalError(null);
    clearMessages();
    const next = relays.map((r, i) => (i === index ? { ...r, ...patch } : r));
    // Don't allow turning both read and write off without removing
    const row = next[index];
    if (row && !row.read && !row.write) {
      setLocalError('Enable read and/or write, or remove the relay');
      return;
    }
    setRelays(next);
  };

  const removeRelay = (index: number) => {
    setLocalError(null);
    clearMessages();
    setRelays(relays.filter((_, i) => i !== index));
  };

  const addBlossom = () => {
    setLocalError(null);
    clearMessages();
    const url = parseBlossomUrlInput(blossomInput);
    if (!url) {
      setLocalError('Enter a valid Blossom URL (https://…)');
      return;
    }
    const key = normalizeNetworkUrl(url);
    if (blossomServers.some((s) => normalizeNetworkUrl(s) === key)) {
      setLocalError('That server is already in the list');
      return;
    }
    setBlossom([...blossomServers, url]);
    setBlossomInput('');
  };

  const removeBlossom = (index: number) => {
    setLocalError(null);
    clearMessages();
    setBlossom(blossomServers.filter((_, i) => i !== index));
  };

  const moveBlossom = (index: number, dir: -1 | 1) => {
    const j = index + dir;
    if (j < 0 || j >= blossomServers.length) return;
    const next = [...blossomServers];
    [next[index], next[j]] = [next[j], next[index]];
    setBlossom(next);
  };

  const resetToAppDefaults = () => {
    if (
      !confirm(
        'Replace your local relay list with OpenDork app defaults? This does not change what is published on your pubkey until you Publish.',
      )
    ) {
      return;
    }
    setLocalError(null);
    clearMessages();
    setRelays(APP_RELAYS.relays.map((r) => ({ ...r })));
  };

  const displayError = localError || error;
  const relayUpdated = formatWhen(remote?.relayEvent?.created_at);
  const blossomUpdated = formatWhen(remote?.blossomEvent?.created_at);

  return (
    <div className="space-y-7">
      <div className="space-y-1.5">
        <p className="text-[13px] text-[#e8e6e3] font-medium">Relays & media</p>
        <p className="text-[12px] text-[#5a5a5a] leading-relaxed">
          OpenDork connects only to the relays below. After login we pull your published NIP-65
          list (kind 10002) and Blossom servers (kind 10063) when available. Pull uses direct
          WebSocket queries to discovery relays (including relay.ditto.pub) and your NIP-65 list —
          not only the live pool. Edit freely; publish when you want other clients to match.
        </p>
      </div>

      {/* Status / errors */}
      {(displayError || status || busy) && (
        <div
          className={cn(
            'rounded-xl border px-3.5 py-2.5 text-[12px] leading-relaxed',
            displayError
              ? 'border-[#3a2a2a] bg-[#1a1212] text-[#c89090]'
              : 'border-[#1f2a1f] bg-[#121a12] text-[#8aba8a]',
          )}
        >
          {busy && !displayError && !status ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Working…
            </span>
          ) : (
            displayError || status
          )}
        </div>
      )}

      {/* Pool summary + actions */}
      <section className="space-y-2.5">
        <div className="flex items-center gap-2">
          <Radio className="w-3.5 h-3.5 text-[#5a5a5a]" />
          <h3 className="text-[12px] font-medium text-[#c8c6c3]">Connection pool</h3>
        </div>
        <div className="rounded-xl border border-[#1f1f1f] bg-[#0f0f0f] px-3.5 py-3 space-y-2.5">
          <p className="text-[11px] text-[#5a5a5a] leading-relaxed flex gap-1.5">
            <Info className="w-3 h-3 shrink-0 mt-0.5" />
            <span>
              {onAppFallback
                ? 'Using OpenDork bootstrap relays until you add your own or pull a NIP-65 list.'
                : `${effective.relays.filter((r) => r.read).length} read · ${effective.relays.filter((r) => r.write).length} write — exclusive pool (no silent merge with app defaults).`}
            </span>
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={!user || busy}
              onClick={() => void pullFromRelays('replace')}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium border border-[#2a2a2a] bg-[#161616] text-[#c8c6c3] hover:bg-[#1c1c1c] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {busy || remoteFetching ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Download className="w-3 h-3" />
              )}
              Pull from pubkey
            </button>
            <button
              type="button"
              disabled={!user || busy || relays.length === 0}
              onClick={() => void publishRelays()}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium border border-[#2a2a2a] bg-[#161616] text-[#c8c6c3] hover:bg-[#1c1c1c] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Upload className="w-3 h-3" />
              Publish relays
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={resetToAppDefaults}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium border border-[#2a2a2a] bg-transparent text-[#5a5a5a] hover:text-[#c8c6c3] hover:bg-[#1a1a1a] disabled:opacity-40 transition-colors"
            >
              <RotateCcw className="w-3 h-3" />
              App defaults
            </button>
          </div>
          {!user && (
            <p className="text-[11px] text-[#3a3a3a]">
              Log in to pull or publish NIP-65 / Blossom lists. You can still edit the local pool
              offline.
            </p>
          )}
          {(remote?.relayEvent || remote?.blossomEvent) && (
            <p className="text-[11px] text-[#3a3a3a]">
              On relays:
              {remote.relayEvent
                ? ` NIP-65${relayUpdated ? ` (${relayUpdated})` : ''} · ${remote.relays.length} relay(s)`
                : ' no NIP-65'}
              {' · '}
              {remote.blossomEvent
                ? `Blossom${blossomUpdated ? ` (${blossomUpdated})` : ''} · ${remote.blossomServers.length} server(s)`
                : 'no kind 10063 yet'}
            </p>
          )}
        </div>
      </section>

      {/* Editable relays */}
      <section className="space-y-2.5">
        <div className="flex items-center gap-2">
          <Radio className="w-3.5 h-3.5 text-[#5a5a5a]" />
          <h3 className="text-[12px] font-medium text-[#c8c6c3]">
            Relays
            <span className="text-[#3a3a3a] font-normal ml-1.5">NIP-65 · kind 10002</span>
          </h3>
        </div>

        {relays.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#242424] px-3.5 py-4 text-[12px] text-[#5a5a5a] leading-relaxed">
            No relays configured. Add one below, pull from your pubkey, or restore app defaults.
            Until then OpenDork uses bootstrap relays under the hood.
          </div>
        ) : (
          <ul className="space-y-1">
            {relays.map((r, i) => (
              <li
                key={`${r.url}-${i}`}
                className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-[#1f1f1f] bg-[#0f0f0f]"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] text-[#e8e6e3] truncate font-medium">
                    {hostLabel(r.url)}
                  </p>
                  <p className="text-[11px] text-[#3a3a3a] truncate font-mono">{r.url}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <RwToggle
                    label="read"
                    on={r.read}
                    onChange={(v) => updateRelay(i, { read: v })}
                    disabled={busy}
                  />
                  <RwToggle
                    label="write"
                    on={r.write}
                    onChange={(v) => updateRelay(i, { write: v })}
                    disabled={busy}
                  />
                  <button
                    type="button"
                    onClick={() => removeRelay(i)}
                    disabled={busy}
                    className="p-1.5 rounded-lg text-[#3a3a3a] hover:text-[#c89090] hover:bg-[#1a1212] transition-colors disabled:opacity-40"
                    title="Remove relay"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="flex gap-2">
          <input
            type="text"
            value={relayInput}
            onChange={(e) => setRelayInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addRelay();
              }
            }}
            placeholder="wss://relay.example.com"
            className="flex-1 min-w-0 px-3 py-2 rounded-xl border border-[#1f1f1f] bg-[#0f0f0f] text-[13px] text-[#e8e6e3] placeholder:text-[#3a3a3a] focus:outline-none focus:border-[#333] font-mono"
          />
          <button
            type="button"
            onClick={addRelay}
            disabled={busy || !relayInput.trim()}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] font-medium border border-[#2a2a2a] bg-[#161616] text-[#c8c6c3] hover:bg-[#1c1c1c] disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
          >
            <Plus className="w-3.5 h-3.5" />
            Add
          </button>
        </div>
      </section>

      {/* Blossom */}
      <section className="space-y-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <HardDrive className="w-3.5 h-3.5 text-[#5a5a5a] shrink-0" />
            <h3 className="text-[12px] font-medium text-[#c8c6c3] truncate">
              Blossom servers
              <span className="text-[#3a3a3a] font-normal ml-1.5">kind 10063</span>
            </h3>
          </div>
          <button
            type="button"
            disabled={!user || busy || blossomServers.length === 0}
            onClick={() => void publishBlossom()}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium border border-[#2a2a2a] bg-[#161616] text-[#c8c6c3] hover:bg-[#1c1c1c] disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
          >
            <Upload className="w-3 h-3" />
            Publish
          </button>
        </div>
        <p className="text-[11px] text-[#3a3a3a] leading-relaxed">
          Media hosts (BUD-03). Order is preference — most trusted first. Stored with your app
          config; publish to advertise on your pubkey.
          {blossomUpdated ? ` Latest on relays: ${blossomUpdated}.` : ''}
        </p>

        {blossomServers.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#242424] px-3.5 py-4 text-[12px] text-[#5a5a5a] leading-relaxed">
            No Blossom servers yet. Pull from your pubkey or add one below.
          </div>
        ) : (
          <ul className="space-y-1">
            {blossomServers.map((url, i) => (
              <li
                key={`${url}-${i}`}
                className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-[#1f1f1f] bg-[#0f0f0f]"
              >
                <div className="flex flex-col gap-0 shrink-0">
                  <button
                    type="button"
                    onClick={() => moveBlossom(i, -1)}
                    disabled={i === 0 || busy}
                    className="p-0.5 text-[#3a3a3a] hover:text-[#c8c6c3] disabled:opacity-20"
                    title="Move up (higher preference)"
                  >
                    <ChevronUp className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveBlossom(i, 1)}
                    disabled={i === blossomServers.length - 1 || busy}
                    className="p-0.5 text-[#3a3a3a] hover:text-[#c8c6c3] disabled:opacity-20"
                    title="Move down"
                  >
                    <ChevronDown className="w-3 h-3" />
                  </button>
                </div>
                <span className="text-[10px] text-[#3a3a3a] font-mono w-5 shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] text-[#e8e6e3] truncate font-medium">
                    {hostLabel(url)}
                  </p>
                  <p className="text-[11px] text-[#3a3a3a] truncate font-mono">{url}</p>
                </div>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 rounded-lg text-[#3a3a3a] hover:text-[#c8c6c3] hover:bg-[#1a1a1a] transition-colors shrink-0"
                  title="Open server"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <button
                  type="button"
                  onClick={() => removeBlossom(i)}
                  disabled={busy}
                  className="p-1.5 rounded-lg text-[#3a3a3a] hover:text-[#c89090] hover:bg-[#1a1212] transition-colors disabled:opacity-40 shrink-0"
                  title="Remove server"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex gap-2">
          <input
            type="text"
            value={blossomInput}
            onChange={(e) => setBlossomInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addBlossom();
              }
            }}
            placeholder="https://cdn.example.com"
            className="flex-1 min-w-0 px-3 py-2 rounded-xl border border-[#1f1f1f] bg-[#0f0f0f] text-[13px] text-[#e8e6e3] placeholder:text-[#3a3a3a] focus:outline-none focus:border-[#333] font-mono"
          />
          <button
            type="button"
            onClick={addBlossom}
            disabled={busy || !blossomInput.trim()}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] font-medium border border-[#2a2a2a] bg-[#161616] text-[#c8c6c3] hover:bg-[#1c1c1c] disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
          >
            <Plus className="w-3.5 h-3.5" />
            Add
          </button>
        </div>
      </section>
    </div>
  );
}
